<template>
  <div class="mx-auto space-y-4 w-full">
    <draggable
      v-model="items"
      handle=".drag-handle"
      class="space-y-4"
      :item-key="itemKey"
      @end="onDragEnd"
      @move="onDragMove"
    >
      <template #item="{ element, index }">
        <div>
          <div :id="`${element.id}-highlight`" class="p-4 bg-white border rounded shadow-sm">
            <Disclosure>
              <div class="grid" :class="itemsCollapsed[element.id] ? '' : 'grid-cols-2'">
                <div v-if="!itemsCollapsed[element.id]">
                  <slot name="collapsed-text" :element="element" :index="index" />
                </div>

                <div>
                  <div class="flex items-center gap-4 my-2" :class="itemsCollapsed[element.id] ? 'justify-center' : 'justify-start'">
                    <DisclosureButton>
                      <div class="flex items-center justify-center">
                        <slot name="collapse-button" :index="index" :element="element" :open="itemsCollapsed[element.id]" :toggle-fn="toggleOpened">
                          <span
                            class="text-gray-600 size-4"
                            :class="itemsCollapsed[element.id] ? 'i-mdi-unfold-less-horizontal' : 'i-mdi-unfold-more-horizontal'"
                            @click="toggleOpened(index)"
                          />
                        </slot>
                      </div>
                    </DisclosureButton>

                    <slot name="drag-handle" :element="element" :index="index">
                      <span
                        class="i-mdi-dots-grid text-gray-600 size-4"
                        :class="[itemsCollapsed[element.id] ? 'cursor-not-allowed' : 'cursor-move drag-handle']"
                      />
                    </slot>

                    <!-- Up Button -->
                    <slot name="up-button" :element="element" :index="index" :move-up-fn="moveUp">
                      <span
                        class="i-heroicons-arrow-up p-1"
                        :class="[index === 0 || itemsCollapsed[element.id] ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer text-gray-600']"
                        :disabled="index === 0 || itemsCollapsed[element.id]"
                        @click="index !== 0 && !itemsCollapsed[element.id] ? moveUp(index) : null"
                      />
                    </slot>

                    <!-- Delete Button -->
                    <slot name="delete-button" :element="element" :index="index" :delete-fn="deleteItem">
                      <span
                        class="i-heroicons-trash text-error cursor-pointer"
                        @click="deleteItem(index, element)"
                      />
                    </slot>

                    <!-- Down Button -->
                    <slot name="down-button" :element="element" :index="index" :move-down-fn="moveDown">
                      <span
                        class="i-heroicons-arrow-down p-1"
                        :class="[index === items.length - 1 || itemsCollapsed[element.id] ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer text-gray-600']"
                        :disabled="index === items.length - 1 || itemsCollapsed[element.id]"
                        @click="index !== items.length - 1 && !itemsCollapsed[element.id] ? moveDown(index) : null"
                      />
                    </slot>
                  </div>
                </div>
              </div>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-out"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div v-show="itemsCollapsed[element.id]">
                  <DisclosurePanel static>
                    <div>
                      <!-- Item Content -->
                      <slot name="item-content" :element="element" :index="index" :open="itemsCollapsed[element.id]" />
                    </div>
                  </DisclosurePanel>
                </div>
              </transition>
            </Disclosure>
          </div>
        </div>
      </template>
    </draggable>
  </div>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ref, watch } from 'vue'

const items = defineModel({
    type: Array,
    default: () => [],
})
const props = defineProps({
  addButtonText: {
    type: String,
    default: 'Add Item',
  },
  itemKey: {
    type: String,
    required: true,
  },
  itemsIdPrefix: {
    type: String,
    required: true,
  },
})

let scrollInterval = null
const emit = defineEmits(['delete'])

const itemsCollapsed = ref({})

items.value.forEach((item) => {
  itemsCollapsed.value[item.id] = true
})

watch(() => items.value, () => {
  items.value.forEach((item) => {
    if (itemsCollapsed.value[item.id] === undefined) {
      itemsCollapsed.value[item.id] = true
    }
  })
})

// Move up and down with automatic scroll
const moveUp = (index: number) => {
  if (index > 0) {
    const temp = items.value[index]
    const newItems = [...items.value]
    newItems.splice(index, 1)
    newItems.splice(index - 1, 0, temp)
    items.value = newItems

    const element = document.getElementById(props.itemsIdPrefix + (index - 1))

    highlightElement(temp.id)
    scrollToElement(element, 'up')
  }
}

const moveDown = (index: number) => {
  if (index < items.value.length - 1) {
    const temp = items.value[index]
    const newItems = [...items.value]
    newItems.splice(index, 1)
    newItems.splice(index + 1, 0, temp)
    items.value = newItems

    const element = document.getElementById(props.itemsIdPrefix + (index + 1))
    highlightElement(temp.id)
    scrollToElement(element, 'down')
  }
}

const highlightElement = (elementId: string) => {
  const element = document.getElementById(`${elementId}-highlight`)
  element.classList.add('highlight-moving')
  setTimeout(() => {
    element.classList.remove('highlight-moving')
  }, 1500)
}

const deleteItem = (index: number, element) => {
  // const newItems = items.value.filter((_, i) => i !== index)
  // items.value = newItems
  emit('delete', {element, index})
}

const scrollToElement = (element: HTMLElement, direction: 'up' | 'down') => {
  const elementRect = element.getBoundingClientRect()
  const offset = 150
  const viewportHeight = window.innerHeight

  const isFullyVisible = elementRect.top >= 0 && elementRect.bottom <= viewportHeight

  if (!isFullyVisible) {
    let scrollTo: number

    if (direction === 'up') {
      const topDistance = elementRect.top
      scrollTo = window.scrollY + topDistance - offset

      if (topDistance < 0) {
        scrollTo = window.scrollY + topDistance - offset
      }

    } else if (direction === 'down') {
      const bottomDistance = elementRect.bottom
      scrollTo = window.scrollY + bottomDistance - viewportHeight + offset

      if (bottomDistance > viewportHeight) {
        scrollTo = window.scrollY + bottomDistance - viewportHeight + offset
      }
    }

    window.scrollTo({ top: scrollTo, behavior: 'smooth' })
  }
}


const onDragEnd = () => {
  if (scrollInterval) clearInterval(scrollInterval)
}

const onDragMove = (event) => {
  const { clientY } = event.originalEvent
  const windowHeight = window.innerHeight
  const scrollThreshold = 100

  if (clientY < scrollThreshold) {
    startAutoScroll(-10)
  } else if (clientY > windowHeight - scrollThreshold) {
    startAutoScroll(10)
  } else {
    stopAutoScroll()
  }
}

const startAutoScroll = (speed: number) => {
  if (!scrollInterval) {
    scrollInterval = setInterval(() => {
      window.scrollBy(0, speed)
    }, 10)
  }
}

const stopAutoScroll = () => {
  if (scrollInterval) {
    clearInterval(scrollInterval)
    scrollInterval = null
  }
}

const toggleOpened = (id) => {
  itemsCollapsed.value[id] = !itemsCollapsed.value[id]
}
</script>

<style scoped>
.highlight-moving {
  border-color: #f6ad55;
  transition: border-color 0.5s;
}
</style>
